import type { CartItem, Total } from '@/js/types/cart.ts';
import type { Option } from '@/js/types/options.ts';
import type { DeliveryPricing, DeliveryType } from '@/js/types/order.ts';
import type { ComputedRef, Ref } from 'vue';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { formatPrice, morph } from '@/js/utils.ts';
import { getTotalName, PRODUCT_WORDS } from '@/js/vue/checkout/data.ts';
import { computed } from 'vue';

type TotalsProps = {
  deliveryPricing: DeliveryPricing
  products: ComputedRef<CartItem[]>
  cartTotals: ComputedRef<Total[]>
  installmentPrice: ComputedRef<number>
  distance: ComputedRef<number>
  isInsideDelivery: Ref<boolean>
  isOutsideDelivery: Ref<boolean>
  deliveryType: ComputedRef<DeliveryType | null>
  pickupPrice: number
};
export function useTotals({ products, cartTotals, installmentPrice, deliveryPricing, distance, isInsideDelivery, isOutsideDelivery, deliveryType, pickupPrice }: TotalsProps) {
  const distanceInKilometers = computed(() => distance.value ? distance.value / 1000 : 0);
  const totalQuantity = computed(() => {
    const quantity = products.value.reduce(
      (acc, curr) => acc + Number(curr.quantity),
      0,
    );
    return `${quantity} ${morph(quantity, ...PRODUCT_WORDS)}`;
  });
  const sumTotal = computed(() => cartTotals.value.find(item => item.type === 'sum'));
  const productTotals = computed((): Option[] => sumTotal.value
    ? [
        {
          label: getTotalName(sumTotal.value.type),
          value: sumTotal.value.value,
        },
      ]
    : []);
  const additionalTotals = computed((): Option[] => {
    return installmentPrice.value > 0
      ? [
          {
            label: getLocaleValue('installation'),
            value: installmentPrice.value,
          },
        ]
      : [];
  });
  const deliveryPriceRangeBySumTotals = computed(() => deliveryPricing.prices.find(item => sumTotal.value?.value >= item.sumFrom && sumTotal.value?.value < item.sumTo));
  const deliveryTotals = computed((): Option[] => {
    if (deliveryType.value === 'pickup') {
      return [
        {
          label: getLocaleValue('delivery_pickup'),
          value: pickupPrice,
        },
      ];
    }
    if (deliveryType.value === 'tk_terminal') {
      return [
        {
          label: getLocaleValue('delivery_tk_terminal'),
          value: deliveryPriceRangeBySumTotals.value?.price ?? 0,
        },
      ];
    }
    if (isInsideDelivery.value && deliveryPriceRangeBySumTotals.value) {
      return [
        {
          label: 'Доставка в пределах МКАДа',
          value: deliveryPriceRangeBySumTotals.value.price,
        },
      ];
    }
    else if (isOutsideDelivery.value && deliveryPriceRangeBySumTotals.value) {
      return [
        {
          label: 'Доставка по московской области',
          value: distanceInKilometers.value * deliveryPricing.outsideRingPrice + deliveryPriceRangeBySumTotals.value.price,
        },
      ];
    }
    return [];
  });
  const orderTotals = computed(() => {
    return [...productTotals.value, ...additionalTotals.value, ...deliveryTotals.value];
  });
  const finalAmount = computed(() => orderTotals.value.reduce((acc, curr) => acc + Number(curr.value), 0));
  const finalPrice = computed(() => formatPrice(finalAmount.value));

  return { totalQuantity, finalAmount, finalPrice, orderTotals, deliveryTotals };
}
